// @ts-strict-ignore
import { AppWidgets } from "@dashboard/apps/components/AppWidgets/AppWidgets";
import {
  getReferenceAttributeEntityTypeFromAttribute,
  mergeAttributeValues,
} from "@dashboard/attributes/utils/data";
import { useUser } from "@dashboard/auth";
import { hasPermission } from "@dashboard/auth/misc";
import { ChannelData } from "@dashboard/channels/utils";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import AssignAttributeValueDialog from "@dashboard/components/AssignAttributeValueDialog";
import { AttributeInput } from "@dashboard/components/Attributes";
import { DashboardCard } from "@dashboard/components/Card";
import ChannelsAvailabilityCard from "@dashboard/components/ChannelsAvailabilityCard";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import { useDevModeContext } from "@dashboard/components/DevModePanel/hooks";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Metadata } from "@dashboard/components/Metadata/Metadata";
import { Savebar } from "@dashboard/components/Savebar";
import { SeoForm } from "@dashboard/components/SeoForm";
import { Tab, TabContainer } from "@dashboard/components/Tab";
import { extensionMountPoints } from "@dashboard/extensions/extensionMountPoints";
import { getExtensionsItemsForProductDetails } from "@dashboard/extensions/getExtensionsItems";
import { useExtensions } from "@dashboard/extensions/hooks/useExtensions";
import {
  ChannelFragment,
  PermissionEnum,
  ProductChannelListingErrorFragment,
  ProductDetailsQuery,
  ProductDetailsVariantFragment,
  ProductErrorCode,
  ProductErrorFragment,
  ProductErrorWithAttributesFragment,
  ProductFragment,
  RefreshLimitsQuery,
  SearchAttributeValuesQuery,
  SearchCategoriesQuery,
  SearchCollectionsQuery,
  SearchPagesQuery,
  SearchProductsQuery,
  TaxClassBaseFragment,
} from "@dashboard/graphql";
import { useBackLinkWithState } from "@dashboard/hooks/useBackLinkWithState";
import { ChangeEvent, SubmitPromise } from "@dashboard/hooks/useForm";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import useStateFromProps from "@dashboard/hooks/useStateFromProps";
import { commonMessages } from "@dashboard/intl";
import { maybe } from "@dashboard/misc";
import ProductExternalMediaDialog from "@dashboard/products/components/ProductExternalMediaDialog";
import { ProductOrganization } from "@dashboard/products/components/ProductOrganization/ProductOrganization";
import { mapByChannel } from "@dashboard/products/components/ProductUpdatePage/utils";
import {
  SECONDARY_CATEGORIES_SAVE_FAILED,
  useSecondaryCategories,
} from "@dashboard/products/hooks/useSecondaryCategories";
import { defaultGraphiQLQuery } from "@dashboard/products/queries";
import {
  productImageUrl,
  productListPath,
  productListUrl,
  productUrl,
  ProductUrlQueryParams,
} from "@dashboard/products/urls";
import { ChoiceWithAncestors, getChoicesWithAncestors } from "@dashboard/products/utils/utils";
import { ProductVariantListError } from "@dashboard/products/views/ProductUpdate/handlers/errors";
import { UseProductUpdateHandlerError } from "@dashboard/products/views/ProductUpdate/handlers/useProductUpdateHandler";
import { TranslationsButton } from "@dashboard/translations/components/TranslationsButton/TranslationsButton";
import { productUrl as createTranslateProductUrl } from "@dashboard/translations/urls";
import { useCachedLocales } from "@dashboard/translations/useCachedLocales";
import { FetchMoreProps, RelayToFlat } from "@dashboard/types";
import WsmProductComposeCard from "@dashboard/wsmCompose/components/WsmProductComposeCard/WsmProductComposeCard";
import { Box, Divider, Option } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { AttributeValuesMetadata, getChoices } from "../../utils/data";
import { ProductDetailsForm } from "../ProductDetailsForm";
import { ProductFitmentTab } from "../ProductFitment";
import { useFitmentExitGuard } from "../ProductFitment/useFitmentExitGuard";
import ProductMedia from "../ProductMedia";
import ProductTaxes from "../ProductTaxes";
import ProductVariants from "../ProductVariants";
import { TopDescriptionCard } from "../TopDescriptionCard/TopDescriptionCard";
import ProductUpdateForm from "./form";
import { messages } from "./messages";
import ProductChannelsListingsDialog from "./ProductChannelsListingsDialog";
import {
  ProductUpdateData,
  ProductUpdateHandlers,
  ProductUpdateSubmitData,
  SubmitResult,
} from "./types";

export interface ProductUpdatePageProps {
  channels: ChannelFragment[];
  productId: string;
  params?: ProductUrlQueryParams;
  channelsErrors: ProductChannelListingErrorFragment[];
  variantListErrors: ProductVariantListError[];
  errors: UseProductUpdateHandlerError[];
  collections: RelayToFlat<SearchCollectionsQuery["search"]>;
  categories: RelayToFlat<SearchCategoriesQuery["search"]>;
  attributeValues: RelayToFlat<SearchAttributeValuesQuery["attribute"]["choices"]>;
  disabled: boolean;
  fetchMoreCategories: FetchMoreProps;
  fetchMoreCollections: FetchMoreProps;
  isMediaUrlModalVisible?: boolean;
  limits: RefreshLimitsQuery["shop"]["limits"];
  variants: ProductDetailsVariantFragment[];
  media: ProductFragment["media"];
  product?: ProductDetailsQuery["product"];
  header: string;
  saveButtonBarState: ConfirmButtonTransitionState;
  taxClasses: TaxClassBaseFragment[];
  fetchMoreTaxClasses: FetchMoreProps;
  referencePages?: RelayToFlat<SearchPagesQuery["search"]>;
  referenceProducts?: RelayToFlat<SearchProductsQuery["search"]>;
  referenceCategories?: RelayToFlat<SearchCategoriesQuery["search"]>;
  referenceCollections?: RelayToFlat<SearchCollectionsQuery["search"]>;
  assignReferencesAttributeId?: string;
  fetchMoreReferencePages?: FetchMoreProps;
  fetchMoreReferenceProducts?: FetchMoreProps;
  fetchMoreAttributeValues?: FetchMoreProps;
  isSimpleProduct: boolean;
  fetchCategories: (query: string) => void;
  fetchCollections: (query: string) => void;
  fetchReferencePages?: (data: string) => void;
  fetchReferenceProducts?: (data: string) => void;
  fetchAttributeValues: (query: string, attributeId: string) => void;
  refetch: () => Promise<any>;
  onAttributeValuesSearch: (id: string, query: string) => Promise<Option[]>;
  onAssignReferencesClick: (attribute: AttributeInput) => void;
  onCloseDialog: () => void;
  onImageDelete: (id: string) => () => void;
  onSubmit: (data: ProductUpdateSubmitData) => SubmitPromise;
  onVariantShow: (id: string) => void;
  onAttributeSelectBlur: () => void;
  onDelete: () => any;
  onImageReorder?: (event: { oldIndex: number; newIndex: number }) => any;
  onImageUpload: (file: File) => any;
  onMediaUrlUpload: (mediaUrl: string) => any;
  onSeoClick?: () => any;
}

export enum ProductUpdatePageTab {
  general = "general",
  variants = "variants",
  fitment = "fitment",
  seo = "seo",
  metadata = "metadata",
  wsm = "wsm",
}

// Create tab components
const GeneralTab = Tab(ProductUpdatePageTab.general);
const VariantsTab = Tab(ProductUpdatePageTab.variants);
const FitmentTab = Tab(ProductUpdatePageTab.fitment);
const SeoTab = Tab(ProductUpdatePageTab.seo);
const MetadataTab = Tab(ProductUpdatePageTab.metadata);
const WsmTab = Tab(ProductUpdatePageTab.wsm);

export const ProductUpdatePage = ({
  productId,
  params,
  disabled,
  categories: categoryChoiceList,
  channels,
  channelsErrors,
  variantListErrors,
  collections: collectionChoiceList,
  isSimpleProduct,
  errors,
  fetchCategories,
  fetchCollections,
  fetchMoreCategories,
  fetchMoreCollections,
  media,
  header,
  limits,
  product,
  saveButtonBarState,
  variants,
  taxClasses,
  fetchMoreTaxClasses,
  referencePages = [],
  referenceProducts = [],
  referenceCategories = [],
  referenceCollections = [],
  onDelete,
  onImageDelete,
  onImageReorder,
  onImageUpload,
  onMediaUrlUpload,
  onVariantShow,
  onSeoClick,
  onSubmit,
  isMediaUrlModalVisible,
  assignReferencesAttributeId,
  onAttributeValuesSearch,
  fetchReferencePages,
  fetchMoreReferencePages,
  fetchReferenceProducts,
  fetchMoreReferenceProducts,
  refetch,
  onCloseDialog,
}: ProductUpdatePageProps) => {
  const intl = useIntl();
  const { user } = useUser();
  const canTranslate = user && hasPermission(PermissionEnum.MANAGE_TRANSLATIONS, user);
  const { lastUsedLocaleOrFallback } = useCachedLocales();
  const navigate = useNavigator();
  const [channelPickerOpen, setChannelPickerOpen] = React.useState(false);
  const [selectedCategory, setSelectedCategory] = useStateFromProps(product?.category?.name || "");
  // const [pdfUploaderMessage, setPdfUploaderMessage] = React.useState<string>("");
  const [mediaUrlModalStatus, setMediaUrlModalStatus] = useStateFromProps(
    isMediaUrlModalVisible || false,
  );
  const [selectedCollections, setSelectedCollections] = useStateFromProps(
    getChoices(maybe(() => product.collections, [])),
  );
  const [selectedTaxClass, setSelectedTaxClass] = useStateFromProps(product?.taxClass?.name ?? "");
  const notify = useNotifier();
  // Set when a save failed somewhere the view's mutation state cannot see (a
  // throw before or during submit, or the secondary-category write after the
  // product saved), so the Savebar does not read "saved" next to the toast.
  const [pageSaveFailed, setPageSaveFailed] = React.useState(false);

  const {
    selectedSecondaryCategories,
    setSelectedSecondaryCategories,
    save: saveSecondaryCategories,
    loading: secondaryCategoriesLoading,
    ready: secondaryCategoriesReady,
    error: secondaryCategoriesError,
    isDirty: secondaryCategoriesDirty,
  } = useSecondaryCategories(product?.id, product?.category?.id);

  // The picker saves with the product form's Save but is not a field of that
  // form, so its edits register with the exit dialog on their own.
  useFitmentExitGuard(secondaryCategoriesDirty);

  // The page's save, registered for the form's native submit and the exit
  // dialog (see ProductUpdateForm's pageSubmitRef); set on every render.
  const pageSubmitRef = React.useRef<(() => SubmitResult) | null>(null);

  // Read failures are shown under the locked picker (makeQuery already
  // toasts the query error itself). A failed save is toasted once, by
  // handleSubmit.
  const handleSecondaryCategoryChange = React.useCallback(
    (event: ChangeEvent, currentPrimaryCategoryId: string | undefined) => {
      // The picker hides the form's current primary from its value, so the
      // list it emits never holds it; keep it selected, so switching the
      // primary back before saving still saves it as secondary.
      setSelectedSecondaryCategories(
        event.target.value as any,
        currentPrimaryCategoryId ? [currentPrimaryCategoryId] : [],
      );
    },
    [setSelectedSecondaryCategories],
  );

  // Tab state management with URL synchronization
  const [activeTab, setActiveTab] = React.useState<ProductUpdatePageTab>(
    (params?.tab as ProductUpdatePageTab) || ProductUpdatePageTab.general,
  );

  const handleTabChange = (tab: ProductUpdatePageTab) => {
    setActiveTab(tab);
    navigate(productUrl(productId, { ...params, tab }));
  };

  const categories = getChoicesWithAncestors(categoryChoiceList);
  const selectedProductCategory = product?.category
    ? getChoicesWithAncestors([product.category as ChoiceWithAncestors])[0]
    : undefined;
  const collections = getChoices(collectionChoiceList);
  const hasVariants = product?.productType?.hasVariants;
  const taxClassesChoices =
    taxClasses?.map(taxClass => ({
      label: taxClass.name,
      value: taxClass.id,
    })) || [];
  const canOpenAssignReferencesAttributeDialog = !!assignReferencesAttributeId;
  const handleAssignReferenceAttribute = (
    attributeValues: AttributeValuesMetadata[],
    data: ProductUpdateData,
    handlers: ProductUpdateHandlers,
  ) => {
    handlers.selectAttributeReference(
      assignReferencesAttributeId,
      mergeAttributeValues(
        assignReferencesAttributeId,
        attributeValues.map(({ value }) => value),
        data.attributes,
      ),
    );
    handlers.selectAttributeReferenceMetadata(assignReferencesAttributeId, attributeValues);
    onCloseDialog();
  };
  const { PRODUCT_DETAILS_MORE_ACTIONS, PRODUCT_DETAILS_WIDGETS } = useExtensions(
    extensionMountPoints.PRODUCT_DETAILS,
  );
  const productErrors = React.useMemo(
    () =>
      errors.filter(
        error => error.__typename === "ProductError",
      ) as ProductErrorWithAttributesFragment[],
    [errors],
  );
  const productOrganizationErrors = React.useMemo(
    () =>
      [...errors, ...channelsErrors].filter(err =>
        ["ProductChannelListingError", "ProductError"].includes(err.__typename),
      ) as Array<ProductErrorFragment | ProductChannelListingErrorFragment>,
    [errors, channelsErrors],
  );
  const extensionMenuItems = getExtensionsItemsForProductDetails(PRODUCT_DETAILS_MORE_ACTIONS, {
    productId: productId,
    productSlug: product?.slug,
  });

  const context = useDevModeContext();
  const openPlaygroundURL = () => {
    context.setDevModeContent(defaultGraphiQLQuery);
    context.setVariables(`{ "id": "${product?.id}" }`);
    context.setDevModeVisibility(true);
  };
  const backLinkProductUrl = useBackLinkWithState({
    path: productListPath,
  });

  return (
    <ProductUpdateForm
      isSimpleProduct={isSimpleProduct}
      onSubmit={onSubmit}
      product={product}
      categories={categories}
      collections={collections}
      selectedCollections={selectedCollections}
      setSelectedCategory={setSelectedCategory}
      setSelectedCollections={setSelectedCollections}
      setSelectedTaxClass={setSelectedTaxClass}
      taxClasses={taxClassesChoices}
      hasVariants={hasVariants}
      referencePages={referencePages}
      referenceProducts={referenceProducts}
      referenceCategories={referenceCategories}
      referenceCollections={referenceCollections}
      fetchReferencePages={fetchReferencePages}
      fetchMoreReferencePages={fetchMoreReferencePages}
      fetchReferenceProducts={fetchReferenceProducts}
      fetchMoreReferenceProducts={fetchMoreReferenceProducts}
      assignReferencesAttributeId={assignReferencesAttributeId}
      disabled={disabled}
      refetch={refetch}
      pageSubmitRef={pageSubmitRef}
    >
      {({ change, data, handlers, submit, isSaveDisabled }) => {
        const handleSubmit = async () => {
          // The primary category rides on productUpdate (input.category, set
          // in getProductUpdateVariables); the secondary set is its own Saleor
          // mutation. The set runs only after the product saved cleanly, and
          // with the primary just saved (data.category), because the server
          // drops whichever category is primary at the moment the set runs.
          setPageSaveFailed(false);

          // One toast per failure, and an error entry in the result so no
          // caller takes the save as clean.
          const fail = (err: unknown, fallback: string, field: string | null) => {
            const message = err instanceof Error && err.message ? err.message : fallback;

            notify({
              status: "error",
              text: message,
            });
            console.error(err);
            setPageSaveFailed(true);

            const error: ProductErrorFragment = {
              __typename: "ProductError",
              code: ProductErrorCode.GRAPHQL_ERROR,
              field,
              message,
            };

            return [error];
          };

          // Without this catch, anything thrown while assembling or sending
          // the product mutations became an unhandled rejection: no request,
          // no notification, a Save button that looked inert.
          const submitResult = await submit().catch(err =>
            fail(err, "Failed to save product", null),
          );

          if (submitResult?.length > 0) {
            // Already toasted by the mutation hooks (payload errors and
            // transport failures alike). A transport failure leaves the
            // view's mutation state reading success, hence the flag.
            setPageSaveFailed(true);

            return submitResult;
          }

          // The product itself saved, so the view's mutation state reads
          // success; a failed set turns the Savebar to error (pageSaveFailed).
          // One outcome toast: the handler's own success toast is off
          // (ProductUpdate passes notifySuccess: false), so success is said
          // here, once both writes landed.
          return saveSecondaryCategories(data.category || undefined).then(
            () => {
              notify({
                status: "success",
                text: intl.formatMessage(commonMessages.savedChanges),
              });

              return submitResult;
            },
            err => fail(err, SECONDARY_CATEGORIES_SAVE_FAILED, "category"),
          );
        };

        pageSubmitRef.current = handleSubmit;

        const availabilityCommonProps = {
          managePermissions: [PermissionEnum.MANAGE_PRODUCTS],
          messages: {
            hiddenLabel: intl.formatMessage({
              id: "saKXY3",
              defaultMessage: "Not published",
              description: "product label",
            }),

            visibleLabel: intl.formatMessage({
              id: "qJedl0",
              defaultMessage: "Published",
              description: "product label",
            }),
          },
          errors: channelsErrors,
          allChannelsCount: channels?.length,
          disabled,
          onChange: handlers.changeChannels,
          openModal: () => setChannelPickerOpen(true),
        };

        const byChannel = mapByChannel(channels);
        const listings = data.channels.updateChannels?.map<ChannelData>(byChannel);

        const entityType = getReferenceAttributeEntityTypeFromAttribute(
          assignReferencesAttributeId,
          data.attributes,
        );

        return (
          <DetailPageLayout>
            <TopNav href={backLinkProductUrl} title={header}>
              {canTranslate && (
                <TranslationsButton
                  marginRight={3}
                  onClick={() =>
                    navigate(createTranslateProductUrl(lastUsedLocaleOrFallback, productId))
                  }
                />
              )}
              <TopNav.Menu
                items={[
                  ...extensionMenuItems,
                  {
                    label: intl.formatMessage(messages.openGraphiQL),
                    onSelect: openPlaygroundURL,
                    testId: "graphiql-redirect",
                  },
                ]}
                dataTestId="menu"
              />
            </TopNav>

            <DetailPageLayout.Content
              style={
                activeTab !== ProductUpdatePageTab.general ? { gridColumn: "1 / -1" } : undefined
              }
            >
              {/* Tab Navigation */}
              <DashboardCard>
                <DashboardCard.Content paddingX={6} paddingY={4}>
                  <TabContainer>
                    <GeneralTab
                      isActive={activeTab === ProductUpdatePageTab.general}
                      changeTab={handleTabChange}
                      testId="general-tab"
                    >
                      {intl.formatMessage(messages.tabGeneral)}
                    </GeneralTab>
                    <VariantsTab
                      isActive={activeTab === ProductUpdatePageTab.variants}
                      changeTab={handleTabChange}
                      testId="variants-tab"
                    >
                      {intl.formatMessage(messages.tabVariants)}
                    </VariantsTab>
                    <FitmentTab
                      isActive={activeTab === ProductUpdatePageTab.fitment}
                      changeTab={handleTabChange}
                      testId="fitment-tab"
                    >
                      {intl.formatMessage(messages.tabFitment)}
                    </FitmentTab>
                    <SeoTab
                      isActive={activeTab === ProductUpdatePageTab.seo}
                      changeTab={handleTabChange}
                      testId="seo-tab"
                    >
                      {intl.formatMessage(messages.tabSeo)}
                    </SeoTab>
                    <MetadataTab
                      isActive={activeTab === ProductUpdatePageTab.metadata}
                      changeTab={handleTabChange}
                      testId="metadata-tab"
                    >
                      {intl.formatMessage(messages.tabMetadata)}
                    </MetadataTab>
                    <WsmTab
                      isActive={activeTab === ProductUpdatePageTab.wsm}
                      changeTab={handleTabChange}
                      testId="wsm-tab"
                    >
                      {intl.formatMessage(messages.tabWsm)}
                    </WsmTab>
                  </TabContainer>
                </DashboardCard.Content>
              </DashboardCard>

              {/* General Tab Content */}
              {activeTab === ProductUpdatePageTab.general && (
                <>
                  <ProductDetailsForm
                    data={data}
                    disabled={disabled}
                    errors={productErrors}
                    onChange={change}
                  />
                  <TopDescriptionCard disabled={disabled} />
                  <ProductMedia
                    media={media}
                    onImageDelete={onImageDelete}
                    onImageReorder={onImageReorder}
                    onImageUpload={onImageUpload}
                    openMediaUrlModal={() => setMediaUrlModalStatus(true)}
                    getImageEditUrl={imageId => productImageUrl(productId, imageId)}
                  />
                  {/* <PdfUploader
                    productId={productId}
                    disabled={disabled}
                    loading={disabled}
                    onUploadSuccess={() => {
                      setPdfUploaderMessage("PDF processed successfully!");
                      setTimeout(() => setPdfUploaderMessage(""), 3000);
                      // Refetch product data to show updated description
                      refetch();
                    }}
                    onUploadError={error => {
                      setPdfUploaderMessage(`Error: ${error}`);
                      setTimeout(() => setPdfUploaderMessage(""), 5000);
                    }}
                  />
                  <div style={{ marginTop: 8, paddingLeft: 24 }}>
                    {pdfUploaderMessage && (
                      <Text color={pdfUploaderMessage.includes("Error") ? "critical1" : "success1"}>
                        {pdfUploaderMessage}
                      </Text>
                    )}
                  </div> */}
                  {/* {data.attributes.length > 0 && (
                    <Attributes
                      attributes={data.attributes}
                      attributeValues={attributeValues}
                      errors={productErrors}
                      loading={disabled}
                      disabled={disabled}
                      onChange={handlers.selectAttribute}
                      onMultiChange={handlers.selectAttributeMultiple}
                      onFileChange={handlers.selectAttributeFile}
                      onReferencesRemove={handlers.selectAttributeReference}
                      onReferencesAddClick={onAssignReferencesClick}
                      onReferencesReorder={handlers.reorderAttributeValue}
                      fetchAttributeValues={fetchAttributeValues}
                      fetchMoreAttributeValues={fetchMoreAttributeValues}
                      onAttributeSelectBlur={onAttributeSelectBlur}
                      richTextGetters={attributeRichTextGetters}
                    />
                  )} */}
                </>
              )}

              {/* Variants & Pricing Tab Content */}
              {activeTab === ProductUpdatePageTab.variants && (
                <>
                  <ProductVariants
                    productId={productId}
                    productName={product?.name}
                    errors={variantListErrors}
                    channels={listings}
                    defaultVariantId={product?.defaultVariant?.id || ""}
                    limits={limits}
                    variants={variants}
                    variantAttributes={product?.productType.variantAttributes}
                    onAttributeValuesSearch={onAttributeValuesSearch}
                    onChange={handlers.changeVariants}
                    onRowClick={onVariantShow}
                  />
                </>
              )}

              {/* Fitment Tab Content */}
              {/* Kept mounted once opened (hidden on other tabs) so unsaved card edits survive a tab switch; */}
              {/* keyed by product so another product's page never inherits them */}
              <ProductFitmentTab
                key={productId}
                productId={productId}
                active={activeTab === ProductUpdatePageTab.fitment}
              />

              {/* SEO & URLs Tab Content */}
              {activeTab === ProductUpdatePageTab.seo && (
                <>
                  <SeoForm
                    errors={productErrors}
                    title={data.seoTitle}
                    titlePlaceholder={data.name}
                    description={data.seoDescription}
                    descriptionPlaceholder={""} // TODO: cast description to string
                    slug={data.slug}
                    slugPlaceholder={data.name}
                    loading={disabled}
                    onClick={onSeoClick}
                    onChange={change}
                    helperText={intl.formatMessage({
                      id: "LKoIB1",
                      defaultMessage:
                        "Add search engine title and description to make this product easier to find",
                    })}
                  />
                </>
              )}

              {/* Metadata Tab Content */}
              {activeTab === ProductUpdatePageTab.metadata && (
                <Metadata data={data} onChange={handlers.changeMetadata} />
              )}

              {/* The option sets, fees and compliance record this product
                  carries. The Catalog lists are the cross-catalog audit view;
                  this is the one a merchant editing a product needs. */}
              {activeTab === ProductUpdatePageTab.wsm && (
                <WsmProductComposeCard productId={productId} productName={product?.name} />
              )}
            </DetailPageLayout.Content>
            {activeTab === ProductUpdatePageTab.general && (
              <DetailPageLayout.RightSidebar>
                <ProductOrganization
                  canChangeType={false}
                  categories={categories}
                  categoryInputDisplayValue={selectedCategory}
                  collections={collections}
                  collectionsInputDisplayValue={selectedCollections}
                  data={data}
                  disabled={disabled}
                  errors={productOrganizationErrors}
                  fetchCategories={fetchCategories}
                  fetchCollections={fetchCollections}
                  fetchMoreCategories={fetchMoreCategories}
                  fetchMoreCollections={fetchMoreCollections}
                  productType={product?.productType}
                  secondaryCategories={selectedSecondaryCategories}
                  secondaryCategoriesLoading={secondaryCategoriesLoading}
                  secondaryCategoriesDisabled={!secondaryCategoriesReady}
                  secondaryCategoriesHelperText={secondaryCategoriesError ?? undefined}
                  onCategoryChange={handlers.selectCategory}
                  onCollectionChange={handlers.selectCollection}
                  onSecondaryCategoryChange={event =>
                    handleSecondaryCategoryChange(event, data.category || undefined)
                  }
                  selectedProductCategory={selectedProductCategory}
                />
                <ChannelsAvailabilityCard {...availabilityCommonProps} channels={listings ?? []} />
                <Box>
                  <ProductTaxes
                    value={data.taxClassId}
                    disabled={disabled}
                    onChange={handlers.selectTaxClass}
                    taxClassDisplayName={selectedTaxClass}
                    taxClasses={taxClasses}
                    onFetchMore={fetchMoreTaxClasses}
                  />
                </Box>
                {PRODUCT_DETAILS_WIDGETS.length > 0 && productId && (
                  <>
                    <Divider />
                    <AppWidgets
                      extensions={PRODUCT_DETAILS_WIDGETS}
                      params={{
                        productId: productId,
                        productSlug: product?.slug,
                      }}
                    />
                  </>
                )}
              </DetailPageLayout.RightSidebar>
            )}

            <Savebar>
              <Savebar.DeleteButton onClick={onDelete} />
              <Savebar.Spacer />
              <Savebar.CancelButton onClick={() => navigate(productListUrl())} />
              <Savebar.ConfirmButton
                transitionState={
                  pageSaveFailed && saveButtonBarState !== "loading" ? "error" : saveButtonBarState
                }
                onClick={handleSubmit}
                disabled={isSaveDisabled || secondaryCategoriesLoading}
              />
            </Savebar>

            {canOpenAssignReferencesAttributeDialog && entityType && (
              <AssignAttributeValueDialog
                entityType={entityType}
                confirmButtonState={"default"}
                products={referenceProducts}
                pages={referencePages}
                collections={referenceCollections}
                categories={referenceCategories}
                attribute={data.attributes.find(({ id }) => id === assignReferencesAttributeId)}
                hasMore={handlers.fetchMoreReferences?.hasMore}
                open={canOpenAssignReferencesAttributeDialog}
                onFetch={handlers.fetchReferences}
                onFetchMore={handlers.fetchMoreReferences?.onFetchMore}
                loading={handlers.fetchMoreReferences?.loading}
                onClose={onCloseDialog}
                onSubmit={attributeValues =>
                  handleAssignReferenceAttribute(
                    attributeValues.map(container => ({
                      value: container.id,
                      label: container.name,
                    })),
                    data,
                    handlers,
                  )
                }
              />
            )}

            <ProductExternalMediaDialog
              product={product}
              onClose={() => setMediaUrlModalStatus(false)}
              open={mediaUrlModalStatus}
              onSubmit={onMediaUrlUpload}
            />
            <ProductChannelsListingsDialog
              channels={channels}
              data={data}
              onClose={() => setChannelPickerOpen(false)}
              open={channelPickerOpen}
              onConfirm={handlers.updateChannelList}
            />
          </DetailPageLayout>
        );
      }}
    </ProductUpdateForm>
  );
};
ProductUpdatePage.displayName = "ProductUpdatePage";
export default ProductUpdatePage;
